import { useSetListsIds } from 'components/Dashboard/MainSearchBar/setListIds';
import { useEffect, useState, useMemo, useRef } from 'react';
import LocalStorage from 'utils/LocalStorage';

function useRecentSearchesTopicPicker() {
  const [recentSearches, setRecentSearches] = useState(() => {
    const stored = LocalStorage.getItem('recentSearches');
    return stored ? JSON.parse(stored) : [];
  });

  const storageString = LocalStorage.getItem('recentSearches') || '[]';

  const recentSearchesStore = useMemo(() => {
    return storageString !== '[]' ? JSON.parse(storageString) : [];
  }, [storageString]);

  const prevStoreStringRef = useRef(JSON.stringify(recentSearchesStore));
  const { setListsIds, getStoredListIds } = useSetListsIds();

  useEffect(() => {
    const storeString = JSON.stringify(recentSearchesStore);
    if (storeString !== prevStoreStringRef.current) {
      setRecentSearches(recentSearchesStore);
      prevStoreStringRef.current = storeString;
    }
  }, [recentSearchesStore]);

  const setRecentSearchesFunction = (recentSearches) => {
    setRecentSearches(recentSearches);
    LocalStorage.setItem('recentSearches', JSON.stringify(recentSearches));
  };

  const addRecentSearchToLocalStorage = ({ freeSearch, topic }) => {
    if (topic) {
      setListsIds(topic.id);
    } else {
      const storedRecentSearches = JSON.parse(LocalStorage.getItem('recentSearches')) || [];
      const filteredSearches = storedRecentSearches.filter((search) => search.value !== freeSearch);
      filteredSearches.push({ value: freeSearch, date: new Date().toISOString() });
      setRecentSearchesFunction(filteredSearches);
    }
  };

  const getRecentSearches = () => {
    const recentSearches = JSON.parse(LocalStorage.getItem('recentSearches')) || [];
    return recentSearches;
  };

  const getRecentSearchesAndKeywordsLists = (keywordsLists) => {
    const recentTopics = getStoredListIds({ includeMasterTopic: true }) ?? [];

    let recentSearchesOrdered = sortAndDeduplicateRecentSearches(recentSearches)?.slice(0, 5);
    let recentTopicsOrdered = recentTopics?.slice(0, 5);

    const recentKeywordsListsOrdered = recentTopicsOrdered
      .map((id) => {
        return keywordsLists.find((keyword) => keyword.id === id);
      })
      .filter((item) => item !== undefined);

    const recentKeywordsListsIds = new Set(recentKeywordsListsOrdered?.map((item) => item.id));
    const filteredKeywordsLists = keywordsLists.filter((keyword) => !recentKeywordsListsIds.has(keyword.id));

    let allTopics = [...recentKeywordsListsOrdered, ...filteredKeywordsLists];
    const masterTopicIndex = allTopics.findIndex((topic) => topic.id === null);
    if (masterTopicIndex !== -1) {
      const masterTopic = allTopics[masterTopicIndex];
      allTopics = [masterTopic, ...allTopics.filter((topic) => topic.id !== null)];
    }

    return { topics: allTopics, searches: recentSearchesOrdered || [] };
  };

  const sortAndDeduplicateRecentSearches = (recentSearches) => {
    const sortedRecentSearches = [...recentSearches].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sortedRecentSearches.filter((item, index, self) => index === self.findIndex((t) => t.value === item.value));
  };

  const measureHeightForFirstNItems = ({ items, limit }) => {
    const fullItemsCount = Math.floor(limit);
    const fractionalPart = limit - fullItemsCount;
    const itemsToMeasure = Math.min(fullItemsCount + (fractionalPart > 0 ? 1 : 0), items.length);
    let totalHeight = 0;

    for (let i = 0; i < itemsToMeasure; i++) {
      const item = items[i];
      if (item) {
        const itemRect = item.getBoundingClientRect();
        const itemStyle = window.getComputedStyle(item);
        const marginBottom = parseFloat(itemStyle.marginBottom) || 0;
        const itemHeight = itemRect.height + marginBottom;

        // If this is the last item and we have a fractional part, only add a portion of its height
        if (i === fullItemsCount && fractionalPart > 0) {
          totalHeight += itemHeight * fractionalPart;
        } else {
          totalHeight += itemHeight;
        }
      }
    }
    return totalHeight;
  };

  return {
    addRecentSearchToLocalStorage,
    getRecentSearches,
    getRecentSearchesAndKeywordsLists,
    recentSearches,
    setRecentSearches: setRecentSearchesFunction,
    measureHeightForFirstNItems,
  };
}

export default useRecentSearchesTopicPicker;
